/**
 * InviteMemberModal Component
 *
 * Modal for inviting users to join a workspace as members.
 * Allows setting permissions for the invited user.
 *
 * MULTI-WORKSPACE MODEL:
 * - Users can own 1 workspace
 * - Users can be members of N workspaces
 * - Invites create MEMBERSHIP, not ownership
 */

import React, { useState } from 'react';
import { X, Mail, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { WorkspacePermissions, DEFAULT_PERMISSIONS } from '../../../types/workspace';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: number;
  workspaceName: string;
  onInviteSuccess?: () => void;
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  workspaceName,
  onInviteSuccess
}) => {
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState<WorkspacePermissions>(DEFAULT_PERMISSIONS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setEmail('');
    setPermissions(DEFAULT_PERMISSIONS);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePermissionChange = (key: keyof WorkspacePermissions, value: number) => {
    setPermissions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!email.trim()) {
      setError('Email é obrigatório');
      return;
    }

    if (!validateEmail(email.trim())) {
      setError('Email inválido');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use axios instance from services (handles auth headers automatically)
      const { default: axiosInstance } = await import('../../../services/axiosConfig');
      const response = await axiosInstance.post(
        `/workspaces/${workspaceId}/invites`,
        {
          email: email.trim().toLowerCase(),
          permissions
        }
      );

      if (response.data.success) {
        setSuccess(true);

        // Call success callback
        if (onInviteSuccess) {
          onInviteSuccess();
        }

        // Close modal after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Erro ao enviar convite';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-[#1a1a1a] border border-[#333333] rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#333333]">
          <div>
            <h2 className="text-xl font-semibold text-white">Convidar Membro</h2>
            <p className="text-sm text-[#888888] mt-1">
              Convidar usuário para {workspaceName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-[#888888] hover:text-white transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="m-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-400 mb-1">Convite enviado!</h4>
              <p className="text-xs text-green-400/80">
                O convite foi enviado para {email}
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="m-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-400 mb-1">Erro</h4>
              <p className="text-xs text-red-400/80">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email do Convidado *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full bg-[#2a2a2a] border border-[#333333] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#666666] focus:outline-none focus:border-[#F4CC04] transition-colors"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <p className="text-xs text-[#666666] mt-1">
                O usuário receberá um email com o convite
              </p>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-sm font-medium text-white mb-3">Permissões</h3>
              <div className="space-y-3">
                {/* Can View Analytics */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.can_view_analytics === 1}
                    onChange={(e) => handlePermissionChange('can_view_analytics', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 rounded bg-[#2a2a2a] border-[#333333] text-[#F4CC04] focus:ring-[#F4CC04] focus:ring-offset-0"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white">Visualizar analytics</div>
                    <div className="text-xs text-[#666666]">Acesso ao dashboard e relatórios</div>
                  </div>
                </label>

                {/* Can Manage Agents */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.can_manage_agents === 1}
                    onChange={(e) => handlePermissionChange('can_manage_agents', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 rounded bg-[#2a2a2a] border-[#333333] text-[#F4CC04] focus:ring-[#F4CC04] focus:ring-offset-0"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white">Gerenciar agentes</div>
                    <div className="text-xs text-[#666666]">Criar/editar agentes e bases de conhecimento</div>
                  </div>
                </label>

                {/* Can Manage Workspace */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.can_manage_workspace === 1}
                    onChange={(e) => handlePermissionChange('can_manage_workspace', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 rounded bg-[#2a2a2a] border-[#333333] text-[#F4CC04] focus:ring-[#F4CC04] focus:ring-offset-0"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white">Gerenciar workspace</div>
                    <div className="text-xs text-[#666666]">Gerenciar membros, grupos e convites</div>
                  </div>
                </label>

                {/* Can Admin */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.can_admin === 1}
                    onChange={(e) => handlePermissionChange('can_admin', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 rounded bg-[#2a2a2a] border-[#333333] text-[#F4CC04] focus:ring-[#F4CC04] focus:ring-offset-0"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white">Administrador</div>
                    <div className="text-xs text-[#666666]">Acesso administrativo completo</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 bg-[#2a2a2a] hover:bg-[#333333] text-white rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-[#F4CC04] hover:bg-[#F4CC04]/90 text-[#1a1a1a] font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#1a1a1a]/20 border-t-[#1a1a1a] rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Convite
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InviteMemberModal;
